import { api } from "./auth";

/**
 * Get current logged-in user's profile
 */
export async function getCurrentUser() {
  const response = await api.get("/users/me");
  return response.data;
}

/**
 * Update current user's profile
 * @param {FormData | Object} userData - User data to update (use FormData for photo uploads)
 */
export async function updateUserProfile(userData) {
  const isFormData = userData instanceof FormData;
  
  const config = isFormData ? {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  } : {};
  
  const response = await api.put("/users/me", userData, config);
  return response.data;
}

/**
 * Get user profile by ID
 * @param {number} userId - User ID
 */
export async function getUserProfileById(userId) {
  const response = await api.get(`/users/profile/${userId}`);
  return response.data;
}

/**
 * Get all partners (excluding current user)
 * Contact info is hidden unless connected
 * @param {Object} filters - Search filters
 * @param {string} [filters.destination] - Filter by destination
 * @param {boolean} [filters.show_all] - Include partners without trips
 * @param {number} [filters.skip] - Number of records to skip (pagination)
 * @param {number} [filters.limit] - Number of records to return (pagination)
 */
export async function getPartners(filters = {}) {
  const params = new URLSearchParams();
  
  if (filters.destination) {
    params.append("destination", filters.destination);
  }
  if (filters.show_all !== undefined) {
    params.append("show_all", filters.show_all);
  }
  if (filters.skip !== undefined) {
    params.append("skip", filters.skip);
  }
  if (filters.limit !== undefined) {
    params.append("limit", filters.limit);
  }
  
  const queryString = params.toString();
  const url = queryString ? `/partners/?${queryString}` : "/partners/";
  
  const response = await api.get(url);
  return response.data;
}

/**
 * Search partners by destination and other filters
 * @param {Object} filters - Search filters
 * @param {string} [filters.destination] - Destination to search
 * @param {number} [filters.min_age] - Minimum age
 * @param {number} [filters.max_age] - Maximum age
 * @param {string} [filters.gender] - Gender filter (male, female, other)
 */
export async function searchPartners(filters = {}) {
  const params = new URLSearchParams();
  
  if (filters.destination) {
    params.append("destination", filters.destination);
  }
  if (filters.min_age !== undefined) {
    params.append("min_age", filters.min_age);
  }
  if (filters.max_age !== undefined) {
    params.append("max_age", filters.max_age);
  }
  if (filters.gender) {
    params.append("gender", filters.gender);
  }
  
  const queryString = params.toString();
  const url = queryString ? `/partners/search?${queryString}` : "/partners/search";
  
  const response = await api.get(url);
  return response.data;
}

/**
 * Get a specific partner by ID
 * @param {number} partnerId - Partner profile ID
 */
export async function getPartnerById(partnerId) {
  const response = await api.get(`/partners/${partnerId}`);
  return response.data;
}

/**
 * Send a connection request to another partner
 * @param {number} receiverPartnerId - The partner profile ID to send request to
 * @param {string} message - Message to include with the request
 */
export async function sendPartnerRequest(receiverPartnerId, message) {
  const response = await api.post("/partners/requests", {
    receiver_partner_id: receiverPartnerId,
    message,
  });
  return response.data;
}

/**
 * Get all sent partner requests
 */
export async function getSentRequests() {
  const response = await api.get("/partners/requests/sent");
  return response.data;
}

/**
 * Get all received partner requests
 */
export async function getReceivedRequests() {
  const response = await api.get("/partners/requests/received");
  return response.data;
}

/**
 * Update a partner request status (accept or reject)
 * @param {number} requestId - The request ID
 * @param {string} status - New status: 'accepted' or 'rejected'
 */
export async function updateRequestStatus(requestId, status) {
  const endpoint = status === "accepted" 
    ? `/partners/requests/${requestId}/accept`
    : `/partners/requests/${requestId}/reject`;
  
  const response = await api.put(endpoint);
  return response.data;
}

/**
 * Accept a partner request
 * @param {number} requestId - The request ID
 */
export async function acceptPartnerRequest(requestId) {
  const response = await api.put(`/partners/requests/${requestId}/accept`);
  return response.data;
}

/**
 * Reject a partner request
 * @param {number} requestId - The request ID
 */
export async function rejectPartnerRequest(requestId) {
  const response = await api.put(`/partners/requests/${requestId}/reject`);
  return response.data;
}

/**
 * Get received agency requests (for partners)
 */
export async function getReceivedAgencyRequests() {
  const response = await api.get("/partners/agency-requests/received");
  return response.data;
}

/**
 * Accept an agency request (for partners)
 * @param {number} requestId - The request ID
 */
export async function acceptAgencyRequest(requestId) {
  const response = await api.put(`/partners/agency-requests/${requestId}/accept`);
  return response.data;
}

/**
 * Reject an agency request (for partners)
 * @param {number} requestId - The request ID
 */
export async function rejectAgencyRequest(requestId) {
  const response = await api.put(`/partners/agency-requests/${requestId}/reject`);
  return response.data;
}

/**
 * Get all connected partners
 */
export async function getConnectedPartners() {
  const partners = await getPartners();
  return partners.filter(partner => partner.is_connected);
}

/**
 * Check if current user is connected to a specific partner
 * @param {number} partnerId - Partner profile ID
 */
export async function isConnectedToPartner(partnerId) {
  try {
    const partner = await getPartnerById(partnerId);
    return partner.is_connected || false;
  } catch (error) {
    console.error("Error checking connection status:", error);
    return false;
  }
}
